/**
 * Phase 187 — Onglet "Parc matériel & Interventions" sur la fiche fournisseur.
 * Réciprocité de LinkedSuppliersBlock : montre tous les matériels liés à ce
 * fournisseur via des entretiens, avec l'historique détaillé des interventions.
 */
import { useEffect, useState } from "react";
import { api, formatDate, formatCurrency } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wrench, ChevronDown, ChevronRight, Package } from "lucide-react";
import { Button } from "@/components/ui/button";

const CATEGORY_LABEL = {
  bureau: "Bureau",
  petit_materiel: "Petit matériel",
  gros_materiel: "Gros matériel",
  vehicules: "Véhicule",
  vetements: "Vêtements",
  epi: "EPI",
};

export const SupplierLinkedEquipmentTab = ({ supplierId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!supplierId) return;
    let cancelled = false;
    setLoading(true);
    api.getSupplierLinkedEquipment(supplierId)
      .then((res) => { if (!cancelled) setRows(res.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [supplierId]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const totalInterventions = rows.reduce((s, r) => s + r.intervention_count, 0);
  const totalCost = rows.reduce((s, r) => s + (r.total_cost || 0), 0);

  return (
    <div className="space-y-4" data-testid="supplier-linked-equipment-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Parc matériel & Interventions
            </CardTitle>
            {rows.length > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline">{rows.length} matériel{rows.length > 1 ? "s" : ""}</Badge>
                <Badge variant="secondary">{totalInterventions} intervention{totalInterventions > 1 ? "s" : ""}</Badge>
                {totalCost > 0 && <Badge className="bg-slate-800">Total : {formatCurrency(totalCost)}</Badge>}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground italic text-center py-6">Chargement…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-6" data-testid="supplier-linked-equipment-empty">
              Aucun matériel lié à ce fournisseur.
              Rendez-vous sur une fiche matériel, ajoutez un entretien et sélectionnez ce fournisseur pour créer un lien.
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div
                  key={r.equipment_id}
                  className="border rounded-md bg-slate-50/40"
                  data-testid={`supplier-linked-equipment-${r.equipment_id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(r.equipment_id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-100/60 transition-colors text-left"
                  >
                    {expanded[r.equipment_id]
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                    <Package className="h-4 w-4 text-slate-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{r.equipment.name || "Sans nom"}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-0.5">
                        <span>{CATEGORY_LABEL[r.equipment.category] || r.equipment.category}</span>
                        {r.equipment.brand && <span>· {r.equipment.brand}</span>}
                        {r.equipment.registration && <span>· <span className="font-mono">{r.equipment.registration}</span></span>}
                        {r.equipment.serial_number && <span>· S/N {r.equipment.serial_number}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.main_reason && (
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={r.main_reason.color ? {
                            borderColor: r.main_reason.color,
                            color: r.main_reason.color,
                            backgroundColor: `${r.main_reason.color}12`,
                          } : {}}
                        >
                          {r.main_reason.name}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {r.intervention_count} intervention{r.intervention_count > 1 ? "s" : ""}
                      </Badge>
                      {r.total_cost > 0 && <Badge className="text-xs bg-slate-700">{formatCurrency(r.total_cost)}</Badge>}
                    </div>
                  </button>

                  {expanded[r.equipment_id] && (
                    <div className="border-t bg-white px-3 py-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-24">Date</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Motif</TableHead>
                            <TableHead>Produit</TableHead>
                            <TableHead className="text-right w-28">Coût</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {r.interventions.map((i) => (
                            <TableRow key={i.id} data-testid={`intervention-${i.id}`}>
                              <TableCell className="text-xs font-mono whitespace-nowrap">{formatDate(i.maintenance_date)}</TableCell>
                              <TableCell className="text-sm">{i.description}</TableCell>
                              <TableCell>
                                {i.reason ? (
                                  <Badge variant="outline" className="text-xs">{i.reason.name}</Badge>
                                ) : <span className="text-xs text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="text-xs">
                                {i.product ? (
                                  <span>
                                    {i.product.name}
                                    {i.product.reference && <span className="text-muted-foreground ml-1">({i.product.reference})</span>}
                                  </span>
                                ) : <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {i.cost ? (
                                  <>
                                    {formatCurrency(i.cost)}
                                    {i.cost_is_ttc === false ? <span className="text-xs text-muted-foreground ml-1">HT</span>
                                      : i.cost_is_ttc === true ? <span className="text-xs text-muted-foreground ml-1">TTC</span> : null}
                                  </>
                                ) : "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplierLinkedEquipmentTab;
